/** 空间页：纯数据结构类型（interface / type alias） */

/** 空间 PC 端导航单条（首项「空间首页」不来自接口，由程序固定） */
export interface SpaceNavItem {
  routeKey: string;
  productKind: "space_home" | string;
  spaceProductId?: string;
  label: string;
}

/** 空间顶区展示：头像、名称、8 位 ID、粉丝数 */
export interface SpaceProfile {
  spaceId: string;
  name: string;
  avatarUrl: string;
  followerCount: number;
  /** 发生过订阅关系的用户数 */
  subscriptionCount: number;
  /** 认证标签展示名；未授予时为 null */
  certLabelName: string | null;
  signature: string | null;
  douyinId: string | null;
  douyinName: string | null;
}

export interface MobileEntranceSlot {
  enabled: boolean;
  sortOrder: number;
  activeIconUrl: string | null;
  deactiveIconUrl: string | null;
}

export interface MobileEntrances {
  diamond: MobileEntranceSlot;
  tabBar: MobileEntranceSlot;
  mine: MobileEntranceSlot;
}

export interface PcEntrance {
  enabled: boolean;
  sortOrder: number | null;
}

export interface ProductPriceSpec {
  tierId: string;
  enabled: boolean;
  specName: string;
  durationLabel: string;
  priceYuan: number;
}

export type IntelCommentKind = "none" | "ai_recommend" | "manual_comment";

export function normalizeIntelCommentKind(raw: unknown): IntelCommentKind {
  const s = typeof raw === "string" ? raw.trim() : "";
  if (s === "ai_recommend" || s === "manual_comment" || s === "none") {
    return s;
  }
  return "none";
}

export interface SpaceModuleConfig {
  spaceProductId: string;
  productKind: string;
  displayName: string;
  activeIconUrl: string | null;
  deactiveIconUrl: string | null;
  description: string;
  mobileEntrances: MobileEntrances;
  pcEntrance: PcEntrance;
  pricingMode: string;
  priceSpecs: ProductPriceSpec[];
  agreementMode: string;
  agreementTemplateId: string;
  /** 移动端购买页商品介绍海报；无或未返回时为 undefined */
  mobilePurchasePosterUrl?: string;
  /** WEB 订阅引导 / PC 购买介绍海报（建议 5:3）；无或未返回时为 undefined */
  webPurchasePosterUrl?: string;
  /** 产品介绍海报（建议 5:3）；无或未返回时为 undefined */
  productIntroPosterUrl?: string;
  /** 赠送依赖的会员产品展示名；仅 pricingMode=gift 时由后端下发 */
  giftTriggerDisplayNames?: string[] | null;
  /** any / all；与赠送规则 memberMatchMode 对齐 */
  giftMemberMatchMode?: string | null;
  /** 情报产品解读模式；非情报或未返回时为 undefined */
  intelCommentKind?: IntelCommentKind | null;
  /** 问答产品是否允许公开；非问答或未返回时为 undefined */
  qaAllowPublic?: boolean | null;
  /** 策略工具：market / bundle */
  strategySaleMode?: "market" | "bundle" | null;
  /** 市场模式可售 STRA 及规格价 */
  strategySkuOffers?: StrategySkuOffer[] | null;
  /** 打包产品：成员产品展示名 */
  bundledProductDisplayNames?: string[] | null;
  /** 打包产品：成员 space_product_id（有序） */
  bundledSpaceProductIds?: string[] | null;
}

export interface StrategySkuOffer {
  subProductItemId: string;
  name: string;
  intro: string;
  priceSpecs: ProductPriceSpec[];
}

export interface StrategyEntitlementItem {
  subProductItemId: string;
  name: string;
  intro: string;
  active: boolean;
  daysRemaining: number | null;
}

export type MembershipProduct = SpaceModuleConfig & {
  userSubscribed: boolean;
  daysRemaining: number | null;
  subscribedStrategyCount?: number | null;
  strategyEntitlements?: StrategyEntitlementItem[] | null;
  /** false 时不展示 7天试用 规格 */
  trialTierVisible?: boolean | null;
  presentation?: { accent: string };
};

export interface HomeRotatingAdItem {
  imageUrl: string;
  jumpUrl: string;
}

export type HomeLivePhase = "live" | "upcoming" | "preview";

export interface HomeLiveBroadcast {
  coverUrl: string;
  liveId: string;
  title: string;
  /** live=正在直播；upcoming=即将到来；preview=直播预告 */
  phase: HomeLivePhase;
  /** 正在直播时的观看人数 */
  viewerCount: number | null;
  /** 即将到来 / 直播预告时的预约人数 */
  reserveCount: number | null;
}

export interface HomeLatestViewpoint {
  imageUrl: string;
  content: string;
  publishTime: string;
  /** 当前用户是否有权查看全文（后端按订阅权益校验） */
  contentAccessible: boolean;
}

export interface HomeCourseItem {
  coverUrl: string;
  title: string;
  learnerCount: number | null;
  videoUrl?: string | null;
}

export interface GetHomeDataResult {
  rotatingAds: HomeRotatingAdItem[];
  live: HomeLiveBroadcast | null;
  latestViewpoint: HomeLatestViewpoint | null;
  courses: HomeCourseItem[] | null;
}

export interface UserSubscriptionItem {
  spaceProductId: string;
  productKind: string;
  displayName: string;
  userSubscribed: boolean;
  daysRemaining: number | null;
  strategySaleMode?: "market" | "bundle" | null;
  subscribedStrategyCount?: number | null;
  strategyEntitlements?: StrategyEntitlementItem[] | null;
  /** false 时不展示 7天试用 规格 */
  trialTierVisible?: boolean | null;
}

export type SpaceFavorState = {
  favorStatus: number;
  subStatus: number;
};
